import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Radio,
  RadioGroup,
  Skeleton,
  SkeletonItem,
  Spinner,
  Text,
  mergeClasses,
} from '@fluentui/react-components';
import { ArrowSyncRegular } from '@fluentui/react-icons';
import { useApplicationStatus, useImageTags, useSetImage } from '@/hooks/useApplicationManagement';
import { useAppPermission } from '@/hooks/useAppPermission';
import { useTelemetry } from '@/hooks/useTelemetry';
import { PermissionBanner } from '@/components/shared/PermissionBanner';
import { useRuntimeVersionTabStyles } from './RuntimeVersionTab.styles';

interface RuntimeVersionTabProps {
  projectName: string;
  appName: string;
}

interface RuntimeVersionApiError {
  code?: string;
  message?: string;
}

const SUCCESS_MESSAGE_TIMEOUT_MS = 4000;

const CUSTOM_IMAGE_VALUE = '__custom__';

const UPDATE_ERROR_TITLES: Record<string, string> = {
  InvalidRuntimeVersion: 'Invalid runtime version',
  ImageTagNotFound: 'Runtime image tag not found',
};

const UPDATE_ERROR_MESSAGES: Record<string, string> = {
  InvalidRuntimeVersion: 'The selected runtime version is no longer valid. Refresh the version list and try again.',
  ImageTagNotFound: 'The selected image tag could not be found in the registry. Refresh the version list and choose another version.',
};

const getRuntimeUpdateError = (error: unknown): RuntimeVersionApiError => {
  if (error && typeof error === 'object') {
    return error as RuntimeVersionApiError;
  }

  if (error instanceof Error) {
    return { message: error.message };
  }

  return { message: 'Failed to update runtime version.' };
};

export const RuntimeVersionTab = ({ projectName, appName }: RuntimeVersionTabProps) => {
  const styles = useRuntimeVersionTabStyles();
  const { trackEvent, trackException } = useTelemetry();
  const { canEdit, isLoading: isPermissionLoading } = useAppPermission();
  const {
    data: imageTags,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useImageTags(projectName, appName);
  const setImageMutation = useSetImage(projectName, appName);
  const { data: applicationStatus } = useApplicationStatus(projectName, appName);

  const [selectedVersion, setSelectedVersion] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<RuntimeVersionApiError | null>(null);
  const [customImage, setCustomImage] = useState('');
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentVersion = useMemo(() => {
    if (!imageTags) {
      return undefined;
    }

    if (imageTags.versions.length === 0) {
      // API returned no versions — derive a placeholder from currentTag
      return imageTags.currentTag
        ? { version: imageTags.currentTag, displayVersion: imageTags.currentTag, current: true }
        : undefined;
    }

    return imageTags.versions.find((version) => version.current)
      ?? imageTags.versions.find((version) => version.version === imageTags.currentTag)
      ?? imageTags.versions[0];
  }, [imageTags]);

  const selectedVersionDetails = useMemo(() => {
    if (!imageTags) {
      return undefined;
    }

    return imageTags.versions.find((version) => version.version === selectedVersion);
  }, [imageTags, selectedVersion]);

  const currentDisplayVersion = currentVersion?.displayVersion ?? currentVersion?.version ?? imageTags?.currentTag ?? 'Unknown';
  const currentImageReference = imageTags
    ? `${imageTags.registry}/${imageTags.repository}:${imageTags.currentTag}`
    : '';

  useEffect(() => {
    trackEvent('settings.runtime.viewed');
  }, [trackEvent]);

  useEffect(() => {
    if (!imageTags) {
      return;
    }

    const nextCurrentVersion = currentVersion?.version ?? imageTags.currentTag;
    setSelectedVersion((previousVersion) => {
      if (!previousVersion) {
        return nextCurrentVersion;
      }

      const selectedVersionStillExists = imageTags.versions.some((version) => version.version === previousVersion);
      return selectedVersionStillExists ? previousVersion : nextCurrentVersion;
    });
  }, [imageTags, currentVersion?.version]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const isCustomSelected = selectedVersion === CUSTOM_IMAGE_VALUE;
  const isVersionChangeSelected = isCustomSelected
    ? customImage.trim().length > 0
    : (!!selectedVersion && selectedVersion !== currentVersion?.version);
  const selectedDisplayVersion = isCustomSelected ? customImage.trim() : (selectedVersionDetails?.displayVersion ?? selectedVersion);
  const updateInProgress = setImageMutation.isPending;
  const disableVersionControls = updateInProgress || isPermissionLoading;
  const showUpdateAction = canEdit && (isVersionChangeSelected || updateInProgress);

  const updateErrorTitle = updateError?.code ? (UPDATE_ERROR_TITLES[updateError.code] ?? 'Failed to update runtime version') : 'Failed to update runtime version';
  const updateErrorMessage = updateError?.code
    ? (UPDATE_ERROR_MESSAGES[updateError.code] ?? updateError.message ?? 'Try again in a moment.')
    : (updateError?.message ?? 'Try again in a moment.');

  const progressStatusText = useMemo(() => {
    const runningStatus = applicationStatus?.properties?.runningStatus;
    const provisioningState = applicationStatus?.properties?.provisioningState;

    if (runningStatus && provisioningState) {
      return `Application status: ${runningStatus} · Provisioning: ${provisioningState}`;
    }

    if (runningStatus) {
      return `Application status: ${runningStatus}`;
    }

    if (provisioningState) {
      return `Provisioning state: ${provisioningState}`;
    }

    return 'The application will restart while the new runtime image is applied.';
  }, [applicationStatus?.properties?.provisioningState, applicationStatus?.properties?.runningStatus]);

  const handleVersionChange = (_event: unknown, data: { value: string }) => {
    setSelectedVersion(data.value);
    if (data.value !== CUSTOM_IMAGE_VALUE) {
      setCustomImage('');
    }
    setUpdateError(null);
    setSuccessMessage(null);
    setImageMutation.reset();
  };

  const handleConfirmUpdate = async () => {
    if (!imageTags || !currentVersion) {
      return;
    }

    const image = isCustomSelected
      ? customImage.trim()
      : `${imageTags.registry}/${imageTags.repository}:${selectedVersion}`;

    if (!image) return;

    const previousVersion = currentVersion.version;
    const nextVersion = isCustomSelected ? customImage.trim() : selectedVersion;

    setIsConfirmOpen(false);
    setUpdateError(null);
    setSuccessMessage(null);

    try {
      const response = await setImageMutation.mutateAsync({ image });
      const isNoOp = response.previousImage === response.targetImage;

      if (!isNoOp) {
        trackEvent('settings.runtime.version_changed', {
          previousVersion,
          newVersion: nextVersion,
        });
      }

      setSuccessMessage(
        isNoOp
          ? `Runtime version is already ${selectedDisplayVersion}.`
          : `Runtime version update to ${selectedDisplayVersion} has started. It may take a few minutes for the new revision to become active.`,
      );

      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }

      successTimerRef.current = setTimeout(() => setSuccessMessage(null), SUCCESS_MESSAGE_TIMEOUT_MS);

      await refetch();
    } catch (mutationError) {
      setUpdateError(getRuntimeUpdateError(mutationError));
      trackException(mutationError, {
        operation: 'settings.runtime.set_image',
        previousVersion,
        newVersion: nextVersion,
      });
    }
  };

  if (isLoading) {
    return (
      <div className={styles.root}>
        <div className={styles.loadingState} role="status">
          <div className={styles.loadingRow} aria-label="Loading runtime version...">
            <Skeleton>
              <SkeletonItem className={styles.skeletonBadge} />
            </Skeleton>
            <div className={styles.currentVersionDetails}>
              <Skeleton>
                <SkeletonItem className={styles.skeletonLineShort} />
              </Skeleton>
              <Skeleton>
                <SkeletonItem className={styles.skeletonLineLong} />
              </Skeleton>
            </div>
          </div>
          <Skeleton aria-label="Loading runtime version...">
            <SkeletonItem className={styles.skeletonOption} />
          </Skeleton>
          <Skeleton aria-label="Loading runtime version...">
            <SkeletonItem className={styles.skeletonOption} />
          </Skeleton>
          <Skeleton aria-label="Loading runtime version...">
            <SkeletonItem className={styles.skeletonOption} />
          </Skeleton>
        </div>
      </div>
    );
  }

  if (isError || !imageTags || !currentVersion) {
    return (
      <div className={styles.root}>
        <MessageBar intent="error" className={styles.stateBar}>
          <MessageBarBody className={styles.stateBody}>
            <MessageBarTitle>Failed to load runtime versions</MessageBarTitle>
            {error instanceof Error ? error.message : 'Try again in a moment.'}
          </MessageBarBody>
          <MessageBarActions>
            <Button appearance="secondary" icon={<ArrowSyncRegular />} onClick={() => void refetch()}>
              Retry
            </Button>
          </MessageBarActions>
        </MessageBar>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div role="status" aria-live="polite">
        {successMessage && (
          <MessageBar intent="success" className={styles.stateBar}>
            <MessageBarBody className={styles.stateBody}>
              <MessageBarTitle>Runtime version updated</MessageBarTitle>
              {successMessage}
            </MessageBarBody>
          </MessageBar>
        )}

        {updateError && (
          <MessageBar intent="error" className={styles.stateBar}>
            <MessageBarBody className={styles.stateBody}>
              <MessageBarTitle>{updateErrorTitle}</MessageBarTitle>
              {updateErrorMessage}
            </MessageBarBody>
          </MessageBar>
        )}
      </div>

      <div className={styles.currentVersionSection}>
        <Text as="h2" weight="semibold">Current runtime version:</Text>
        <Badge
          appearance="filled"
          color="brand"
          className={styles.currentVersionBadge}
          aria-label={`Current runtime version ${currentDisplayVersion}`}
        >
          {currentDisplayVersion}
        </Badge>
        <Text className={styles.registryInfoText}>{currentImageReference}</Text>
      </div>

      {updateInProgress && (
        <div className={styles.progressSection} role="status" aria-live="polite">
          <Spinner size="small" labelPosition="after" label="Updating runtime version..." />
          <div className={styles.progressCopy}>
            <Text weight="semibold">Updating runtime version...</Text>
            <Text className={styles.registryInfoText}>{progressStatusText}</Text>
          </div>
        </div>
      )}

      {!canEdit ? (
        <PermissionBanner
          title="Read-only access"
          message="You can view the current runtime version, but Contributor access is required to change it."
        />
      ) : (
        <div className={styles.versionPickerArea}>
          <Text as="h2" weight="semibold">Available runtime versions</Text>
            <RadioGroup
              aria-label="Available runtime versions"
              value={selectedVersion}
              onChange={handleVersionChange}
              disabled={disableVersionControls}
              className={styles.radioGroup}
            >
              {imageTags.versions.map((version) => {
                const isSelected = version.version === selectedVersion;

                return (
                  <div
                    key={version.version}
                    className={mergeClasses(
                      styles.versionOption,
                      isSelected && styles.versionOptionSelected,
                    )}
                  >
                    <Radio
                      value={version.version}
                      className={styles.versionRadio}
                      label={
                        <div className={styles.versionOptionContent}>
                          <div className={styles.versionOptionHeader}>
                            <Text weight="medium">{version.displayVersion}</Text>
                            {version.current && (
                              <Badge appearance="tint" color="brand" className={styles.currentTagIndicator}>
                                Current
                              </Badge>
                            )}
                          </div>
                          <Text className={styles.registryInfoText}>
                            {`${imageTags.registry}/${imageTags.repository}:${version.version}`}
                          </Text>
                        </div>
                      }
                    />
                  </div>
                );
              })}

              {/* Custom image option */}
              <div
                className={mergeClasses(
                  styles.versionOption,
                  isCustomSelected && styles.versionOptionSelected,
                )}
              >
                <Radio
                  value={CUSTOM_IMAGE_VALUE}
                  className={styles.versionRadio}
                  label={
                    <div className={styles.versionOptionContent}>
                      <div className={styles.versionOptionHeader}>
                        <Text weight="medium">Custom image</Text>
                      </div>
                      <Text className={styles.registryInfoText}>
                        Specify a full image reference (registry/repository:tag)
                      </Text>
                    </div>
                  }
                />
              </div>
              {isCustomSelected && (
                <Field
                  label="Image reference"
                  className={styles.customImageField}
                  hint="Example: mcr.microsoft.com/azurelogicapps/otto-base:stable"
                >
                  <Input
                    value={customImage}
                    onChange={(_e, data) => {
                      setCustomImage(data.value);
                      setUpdateError(null);
                    }}
                    placeholder="registry/repository:tag"
                    disabled={disableVersionControls}
                    aria-label="Custom image reference"
                  />
                </Field>
              )}
            </RadioGroup>

            {imageTags.versions.length === 0 && (
              <Text className={styles.emptyState}>No runtime versions are currently available.</Text>
            )}

            {showUpdateAction && (
              <div className={styles.updateButtonArea}>
                <Text className={styles.registryInfoText}>
                  Updating the runtime version restarts the application.
                  {isFetching ? ' Refreshing available versions…' : ''}
                </Text>
                <Button
                  appearance="primary"
                  onClick={() => setIsConfirmOpen(true)}
                  disabled={!isVersionChangeSelected || disableVersionControls}
                >
                  Update
                </Button>
              </div>
            )}
          </div>
      )}

      <Dialog open={isConfirmOpen} onOpenChange={(_event, data) => { if (!data.open) setIsConfirmOpen(false); }}>
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogTitle>Update runtime version?</DialogTitle>
            <DialogContent>
              <div className={styles.confirmationSummary}>
                <Text>
                  Are you sure you want to update to version {selectedDisplayVersion}? This will restart the application.
                </Text>
                <div className={styles.confirmationRow}>
                  <Text weight="semibold">Current version:</Text>
                  <Badge appearance="ghost">{currentDisplayVersion}</Badge>
                  <Text>→</Text>
                  <Badge appearance="filled" color="brand">{selectedDisplayVersion}</Badge>
                </div>
                <Text className={styles.registryInfoText}>
                  It may take a few minutes for the new revision to become active.
                </Text>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setIsConfirmOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={() => void handleConfirmUpdate()}>
                Confirm
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};